import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard } from '../../api/adminApi';
import { toTitleCase } from '../../utils/format';

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminDashboard() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="body" style={{ color: 'var(--muted)', padding: 40 }}>Loading…</div>;
  if (!data)   return <div className="body" style={{ color: 'var(--orange)', padding: 40 }}>Failed to load dashboard.</div>;

  const { stats, recent_courses, recent_students } = data;

  return (
    <>
      <div className="ph">
        <div>
          <div className="bc">Admin / Dashboard</div>
          <div className="pt">Welcome back, Admin 👋</div>
          <div className="ps">Manage your learning platform at a glance</div>
        </div>
      </div>

      <div className="body">
        <div className="stats">
          <div className="sc" style={{ animationDelay: '0s' }}>
            <div className="sc-l">Total Courses</div>
            <div className="sc-v">{stats.courses}</div>
            <div className="sc-s" style={{ color: 'var(--green)' }}>{stats.published} published</div>
          </div>
          <div className="sc" style={{ animationDelay: '.05s' }}>
            <div className="sc-l">Instructors</div>
            <div className="sc-v">{stats.instructors}</div>
            <div className="sc-s" style={{ color: 'var(--green)' }}>{stats.active_instructors} active</div>
          </div>
          <div className="sc" style={{ animationDelay: '.1s' }}>
            <div className="sc-l">Students</div>
            <div className="sc-v">{stats.students}</div>
            <div className="sc-s" style={{ color: 'var(--green)' }}>{stats.active_students} active</div>
          </div>
          <div className="sc" style={{ animationDelay: '.15s' }}>
            <div className="sc-l">Certificates</div>
            <div className="sc-v">{stats.certificates}</div>
            <div className="sc-s" style={{ color: 'var(--muted)' }}>
              {stats.students > 0 ? `${Math.round(stats.certificates / stats.students * 100)}% of students` : 'issued'}
            </div>
            <div className="sc-bar">
              <div className="sc-fill" style={{
                width: `${stats.students > 0 ? Math.min(Math.round(stats.certificates / stats.students * 100), 100) : 0}%`,
                background: 'var(--yellow)',
              }} />
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* Recent Courses */}
          <div className="card">
            <div className="ct">
              <span>Recent Courses</span>
              <button className="btn btn-o btn-sm" onClick={() => navigate('/admin/courses')}>View All →</button>
            </div>
            {recent_courses.length === 0 ? (
              <div className="empty"><div className="empty-icon">◫</div><div>No courses yet</div></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12 }}>
                {recent_courses.map((c, i) => (
                  <div
                    key={c.id}
                    className="cc"
                    style={{ animation: `slideUp .3s ease both`, animationDelay: `${i * 0.08}s` }}
                    onClick={() => navigate(`/admin/courses/${c.id}`)}
                  >
                    <div className="cc-thumb" style={{ background: `linear-gradient(135deg,${c.accent_color}22,${c.accent_color}44)` }}>
                      <span style={{ fontSize: 40 }}>{c.emoji}</span>
                    </div>
                    <div className="cc-body">
                      <div className="cc-title">{toTitleCase(c.title)}</div>
                      <div className="cc-meta">
                        {c.enrolled_count}/{c.capacity || 30} students enrolled
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Students */}
          <div className="card">
            <div className="ct">
              <span>Recent Students</span>
              <button className="btn btn-o btn-sm" onClick={() => navigate('/admin/students')}>View All →</button>
            </div>
            {recent_students.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '24px', textAlign: 'center' }}>No students yet</div>
            ) : (
              recent_students.map(s => (
                <div
                  key={s.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/students/${s.id}`)}
                >
                  <div className="av" style={{ background: 'var(--orange)', color: 'white', flexShrink: 0 }}>
                    {(s.full_name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{toTitleCase(s.full_name)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{s.email} · {s.enrollment_count} course{s.enrollment_count !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)' }}>{fmtDate(s.joined_at)}</div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </>
  );
}
